import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BubbleChart from '../components/BubbleChart';
import BarChart from '../components/BarChart';
import Treemap from '../components/Treemap';
import DonutChart from '../components/DonutChart';
import Footer from '../components/Footer';
import Header from '../components/Header';
import FilterMenu, { BubbleLayout, ViewMode, Shape as FilterShape } from '../components/FilterMenu';
import ComparisonPanel from '../components/ComparisonPanel';
import ShareSnapshot from '../components/ShareSnapshot';
import ToolSchema from '../components/ToolSchema';
import AnimationSelector, { AnimationStyle } from '../components/AnimationSelector';
import BubbleTooltip from '../components/BubbleTooltip';
import { TrendingTopic, CryptoTimeframe, FAQ } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, Search, X, ChevronsLeft, ChevronsRight } from 'lucide-react';

type SortField = 'name' | 'category' | 'searchVolume' | 'rank' | 'pubDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface PageData {
  id: string;
  page_url: string;
  source: string;
  meta_title: string;
  meta_description: string;
  intro_text?: string;
  created_at: string;
  summary?: string;
  faq?: string;
  cover_image?: string;
  category?: string;
}

function DynamicPage() {
  const { '*': urlPath } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, user, logout } = useAuth();
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [topics, setTopics] = useState<TrendingTopic[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  // Initialize maxBubbles from URL parameter "Top" or default
  const getInitialMaxBubbles = () => {
    const topParam = searchParams.get('Top');
    if (topParam) {
      const parsed = parseInt(topParam, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return window.innerWidth < 768 ? 40 : 100;
  };

  const [maxBubbles, setMaxBubbles] = useState<number>(getInitialMaxBubbles());
  const [dateFilter, setDateFilter] = useState<'now' | 'all' | '24h' | 'week' | 'month' | 'year'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('bubble');
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme === 'dark' || savedTheme === 'light') ? savedTheme : 'light';
  });
  const [nextBubbleIn, setNextBubbleIn] = useState<string>('');
  const [bubbleProgress, setBubbleProgress] = useState<number>(0);
  const [oldestBubbleTime, setOldestBubbleTime] = useState<number | null>(null);
  const [oldestBubbleCreated, setOldestBubbleCreated] = useState<number | null>(null);
  const [oldestBubbleLifetime, setOldestBubbleLifetime] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sources, setSources] = useState<Array<{value: string, label: string}>>([
    { value: 'all', label: 'All' },
    { value: 'google_trends', label: 'Google Trends' },
    { value: 'user_upload', label: 'My Uploads' }
  ]);
  const [latestPages, setLatestPages] = useState<PageData[]>([]);
  const [allLatestPages, setAllLatestPages] = useState<PageData[]>([]);
  const [sortField, setSortField] = useState<SortField>('searchVolume');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [comparingTopics, setComparingTopics] = useState<Set<string>>(new Set());
  const [bubbleLayout, setBubbleLayout] = useState<BubbleLayout>('force');
  const [shape, setShape] = useState<FilterShape>('bubble');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cryptoTimeframe, setCryptoTimeframe] = useState<CryptoTimeframe>('1h');
  const bubbleChartRef = useRef<HTMLDivElement>(null);
  const treemapChartRef = useRef<HTMLDivElement>(null);
  const donutChartRef = useRef<HTMLDivElement>(null);
  const barChartRef = useRef<HTMLDivElement>(null);
  const [animationStyle, setAnimationStyle] = useState<AnimationStyle>('default');
  const [topSearchQuery, setTopSearchQuery] = useState<string>('');
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [listTooltipData, setListTooltipData] = useState<{ topic: TrendingTopic; x: number; y: number; rank: number } | null>(null);

  const itemsPerPage = 10;

  const sortedTopics = useMemo(() => {
    if (pageData?.source !== 'coingecko_crypto' || !topics.length) {
      return topics;
    }

    return [...topics].sort((a, b) => {
      if (!a.crypto_data || !b.crypto_data) return 0;

      const timeframeMap = {
        '1h': 'change_1h',
        '24h': 'change_24h',
        '7d': 'change_7d',
        '30d': 'change_30d',
        '1y': 'change_1y',
      } as const;

      const fieldName = timeframeMap[cryptoTimeframe];
      const aValue = Math.abs(a.crypto_data[fieldName]);
      const bValue = Math.abs(b.crypto_data[fieldName]);

      return bValue - aValue;
    });
  }, [topics, cryptoTimeframe, pageData?.source]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        loadPageData(),
        loadCategories(),
        loadSources()
      ]);
    };
    loadAllData();
  }, [urlPath]);

  useEffect(() => {
    if (pageData) {
      loadLatestPages(pageData.category, pageData.page_url);
      loadAllLatestPages(pageData.page_url);
    }
  }, [pageData?.category, pageData?.page_url]);

  useEffect(() => {
    const prerenderFooter = document.getElementById('prerender-footer');
    if (prerenderFooter) {
      prerenderFooter.remove();
    }

    // Remove prerendered meta tags to prevent duplicates with React Helmet
    const prerenderedTags = document.head.querySelectorAll('[data-prerendered]');
    prerenderedTags.forEach(tag => tag.remove());
  }, []);

  useEffect(() => {
    document.documentElement.style.backgroundColor = theme === 'dark' ? '#111827' : '#f1f3f4';
  }, [theme]);

  // Sync maxBubbles with URL parameter
  useEffect(() => {
    const topParam = searchParams.get('Top');
    if (topParam) {
      const parsed = parseInt(topParam, 10);
      if (!isNaN(parsed) && parsed > 0 && parsed !== maxBubbles) {
        setMaxBubbles(parsed);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const bubbleInterval = setInterval(() => {
      if (oldestBubbleTime && oldestBubbleCreated && oldestBubbleLifetime) {
        const now = Date.now();
        const remaining = oldestBubbleTime - now;
        const elapsed = now - oldestBubbleCreated;
        const progress = Math.min(100, Math.max(0, (elapsed / oldestBubbleLifetime) * 100));

        setBubbleProgress(progress);

        if (remaining > 0) {
          const seconds = Math.ceil(remaining / 1000);
          setNextBubbleIn(`${seconds}s`);
        } else {
          setNextBubbleIn('0s');
        }
      } else {
        setNextBubbleIn('--');
        setBubbleProgress(0);
      }
    }, 500);
    return () => clearInterval(bubbleInterval);
  }, [oldestBubbleTime, oldestBubbleCreated, oldestBubbleLifetime]);

  useEffect(() => {
    if (pageData) {
      loadTopics();
    }
  }, [dateFilter, categoryFilter, pageData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, categoryFilter, sourceFilter, searchQuery, cryptoTimeframe, topics.length, topSearchQuery]);

  // Handle hash navigation
  useEffect(() => {
    if (!loading && window.location.hash) {
      const hash = window.location.hash.substring(1);
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [loading, urlPath]);

  const loadPageData = async () => {
    try {
      const rawPath = '/' + (urlPath || '');
      const hasTrailingSlash = rawPath.endsWith('/');

      // Normalize to have trailing slash (except root)
      let fullPath = rawPath.replace(/\/+$/, '') + '/';
      if (fullPath === '//') fullPath = '/';

      // Redirect to trailing slash version if missing (for SEO canonicalization)
      if (!hasTrailingSlash && rawPath !== '/') {
        navigate(fullPath, { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('page_url', fullPath)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setPageData(null);
        setLoading(false);
        return;
      }

      setPageData(data);
      await loadFAQs(data.id);
    } catch (error) {
      console.error('Error loading page data:', error);
      setPageData(null);
      setLoading(false);
    }
  };

  const loadFAQs = async (pageId: string) => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('page_id', pageId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      setFaqs([]);
    }
  };

  useEffect(() => {
    if (pageData?.shape) {
      setShape(pageData.shape as FilterShape);
    }
  }, [pageData]);

  const handleThemeChange = (newTheme: 'dark' | 'light') => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.style.backgroundColor = newTheme === 'dark' ? '#111827' : '#f1f3f4';
  };

  const handleMaxBubblesChange = (newMax: number) => {
    setMaxBubbles(newMax);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('Top', newMax.toString());
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleBubbleTimingUpdate = (nextPopTime: number | null, createdTime?: number, lifetime?: number) => {
    setOldestBubbleTime(nextPopTime);
    setOldestBubbleCreated(createdTime || null);
    setOldestBubbleLifetime(lifetime || null);
  };

  const loadTopics = async () => {
    if (!pageData) return;

    try {
      let query = supabase
        .from('trending_topics')
        .select('*');

      if (dateFilter === 'now') {
        const now = new Date();
        const recentDate = new Date();
        recentDate.setHours(now.getHours() - 12);
        query = query.gte('last_seen', recentDate.toISOString());
      } else if (dateFilter !== 'all') {
        const now = new Date();
        let startDate = new Date();

        switch (dateFilter) {
          case '24h':
            startDate.setHours(now.getHours() - 24);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }

        query = query.gte('first_seen', startDate.toISOString());
      }

      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      if (pageData.source !== 'all') {
        query = query.eq('source', pageData.source);
      }

      const { data, error } = await query.order('rank', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedTopics: TrendingTopic[] = data.map(topic => ({
          name: topic.name,
          searchVolume: topic.search_volume,
          searchVolumeRaw: topic.search_volume_raw,
          url: topic.url,
          createdAt: topic.created_at,
          pubDate: topic.pub_date,
          category: topic.category,
          source: topic.source,
          crypto_data: topic.crypto_data,
          note: topic.note
        }));

        const sortedTopics = [...formattedTopics].sort((a, b) => {
          return b.searchVolume - a.searchVolume;
        });

        setTopics(sortedTopics);
      } else {
        setTopics([]);
      }
    } catch (error) {
      console.error('Error loading topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_categories')
        .select('name')
        .order('name');

      if (error) throw error;

      if (data) {
        const categoryNames = data.map(item => item.name);
        setCategories(categoryNames);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_sources')
        .select('value, label')
        .order('label');

      if (error) throw error;

      const defaultSources = [
        { value: 'all', label: 'All' },
        { value: 'google_trends', label: 'Google Trends' },
        { value: 'user_upload', label: 'My Uploads' }
      ];

      if (data) {
        const customSources = data.map(item => ({ value: item.value, label: item.label }));
        setSources([...defaultSources, ...customSources]);
      } else {
        setSources(defaultSources);
      }
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const loadLatestPages = async (category: string | null, currentPageUrl: string) => {
    try {
      if (!category) {
        setLatestPages([]);
        return;
      }

      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('category', category)
        .neq('page_url', currentPageUrl)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      if (data) {
        setLatestPages(data);
      }
    } catch (error) {
      console.error('Error loading category pages:', error);
    }
  };

  const loadAllLatestPages = async (currentPageUrl: string) => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .neq('page_url', currentPageUrl)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      if (data) {
        setAllLatestPages(data);
      }
    } catch (error) {
      console.error('Error loading all latest pages:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredTopics = () => {
    return topics.filter(topic => {
      const matchesSearch = !searchQuery || topic.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || topic.category === categoryFilter;
      const matchesSource = sourceFilter === 'all' || topic.source === sourceFilter;
      return matchesSearch && matchesCategory && matchesSource;
    });
  };

  const getSortedTopics = () => {
    const filtered = getFilteredTopics();
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'category':
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
          break;
        case 'searchVolume':
          aValue = a.searchVolume;
          bValue = b.searchVolume;
          break;
        case 'rank':
          aValue = topics.indexOf(a);
          bValue = topics.indexOf(b);
          break;
        case 'pubDate':
          aValue = a.pubDate ? new Date(a.pubDate).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          bValue = b.pubDate ? new Date(b.pubDate).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="opacity-50" />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <>
        <Header
          theme={theme}
          isAdmin={isAdmin}
          isLoggedIn={!!user}
          onLoginClick={() => {}}
          onLogout={logout}
          title="Top Best Charts"
        />
        <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} px-4`}>
          <div className="text-center max-w-md">
            <h1 className="text-4xl font-bold mb-4">Page not found</h1>
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Looks like you've followed a broken link or entered a URL that doesn't exist on this site.
            </p>
            <a
              href="/"
              className={`inline-block px-6 py-3 rounded-lg font-semibold transition-colors ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow text-white'}`}
            >
              Back to home
            </a>
          </div>
        </div>
        <Footer key={`footer-${theme}`} theme={theme} />
      </>
    );
  }

  // First, sort all topics by searchVolume and assign original ranks
  const allTopicsSorted = [...topics].sort((a, b) => b.searchVolume - a.searchVolume);
  const topicsWithRanks = allTopicsSorted.map((topic, index) => ({
    ...topic,
    originalRank: index + 1
  }));

  // Then filter by search query while preserving original ranks
  const topTopics = topicsWithRanks.filter(topic => {
    if (!topSearchQuery.trim()) return true;
    const query = topSearchQuery.toLowerCase();
    return topic.name.toLowerCase().includes(query) ||
           topic.category?.toLowerCase().includes(query);
  });

  // For crypto pages, split into gainers and losers
  const isCryptoPage = pageData?.source === 'coingecko_crypto';
  let topGainers: TrendingTopic[] = [];
  let topLosers: TrendingTopic[] = [];

  if (isCryptoPage) {
    const getCryptoChange = (topic: TrendingTopic): number => {
      if (!topic.crypto_data) return 0;
      const timeframeMap = {
        '1h': 'change_1h',
        '24h': 'change_24h',
        '7d': 'change_7d',
        '30d': 'change_30d',
        '1y': 'change_1y',
      };
      const field = timeframeMap[cryptoTimeframe] as keyof typeof topic.crypto_data;
      return topic.crypto_data[field] || 0;
    };

    const sortedByChange = [...topics].sort((a, b) => getCryptoChange(b) - getCryptoChange(a));
    const gainers = sortedByChange.filter(t => getCryptoChange(t) > 0).map((topic, index) => ({
      ...topic,
      originalRank: index + 1
    }));
    const losers = sortedByChange.filter(t => getCryptoChange(t) < 0).reverse().map((topic, index) => ({
      ...topic,
      originalRank: index + 1
    }));

    // Filter by search query if present
    const filteredGainers = topSearchQuery.trim()
      ? gainers.filter(topic => {
          const query = topSearchQuery.toLowerCase();
          return topic.name.toLowerCase().includes(query) ||
                 topic.category?.toLowerCase().includes(query);
        })
      : gainers;

    const filteredLosers = topSearchQuery.trim()
      ? losers.filter(topic => {
          const query = topSearchQuery.toLowerCase();
          return topic.name.toLowerCase().includes(query) ||
                 topic.category?.toLowerCase().includes(query);
        })
      : losers;

    const startIndexGainers = (currentPage - 1) * itemsPerPage;
    const endIndexGainers = startIndexGainers + itemsPerPage;
    topGainers = filteredGainers.slice(startIndexGainers, endIndexGainers);
    topLosers = filteredLosers.slice(startIndexGainers, endIndexGainers);
  }

  const totalPages = Math.ceil(topTopics.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayTopics = topTopics.slice(startIndex, endIndex);

  const topTopicNames = topTopics.slice(0, 5).map(t => t.name.replace(/"/g, '')).join(', ');
  const lastUpdated = topics.length > 0 ? new Date(Math.max(...topics.map(t => new Date(t.pubDate || t.createdAt || Date.now()).getTime()))) : new Date();

  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://topbestcharts.com';
  const pageUrl = typeof window !== 'undefined'
    ? window.location.href.replace(/\/$/, '') + '/'
    : `${baseUrl}${pageData.page_url}/`;
  const sourceName = sources.find(s => s.value === pageData.source)?.label || 'Custom';

  const extractTopicType = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('trending topics')) {
      return title.replace(/trending topics/i, '').trim();
    }
    return title;
  };

  const topicType = extractTopicType(pageData.meta_title);
  const topTitle = `Top ${topTopics.length} ${topicType || 'Trending Topics'} (${lastUpdated.getFullYear()})`;

  const enhancedTitle = pageData.meta_title;
  const enhancedDescription = topTopicNames
    ? `${pageData.meta_description} Top trending: ${topTopicNames}. Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}.`
    : pageData.meta_description;

  const keywords = topTopics.slice(0, 10).map(t => t.name.replace(/"/g, '')).join(', ') + ', trending topics, search trends, real-time trends, trend analysis';

  return (
    <>
      <Helmet>
        <title>{enhancedTitle}</title>
        <meta name="description" content={enhancedDescription} />
        <style>{`
          main {
            contain: layout style;
          }
          main > * {
            will-change: auto;
          }
        `}</style>
        <meta name="keywords" content={keywords} />
        <meta name="author" content="Top Best Charts" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={enhancedTitle} />
        <meta property="og:description" content={enhancedDescription} />
        <meta property="og:site_name" content="Top Best Charts" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:updated_time" content={lastUpdated.toISOString()} />
        {pageData.cover_image && <meta property="og:image" content={pageData.cover_image} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={enhancedTitle} />
        <meta name="twitter:description" content={enhancedDescription} />
        {pageData.cover_image && <meta name="twitter:image" content={pageData.cover_image} />}

        <meta property="article:published_time" content={pageData.created_at} />
        <meta property="article:modified_time" content={lastUpdated.toISOString()} />
        <meta property="article:section" content="Trending Topics" />
        <meta property="article:tag" content={topTopicNames} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": enhancedTitle,
            "description": enhancedDescription,
            "url": pageUrl,
            "datePublished": pageData.created_at,
            "dateModified": lastUpdated.toISOString(),
            "author": {
              "@type": "Organization",
              "name": "Google Trending Topics"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Top Best Charts",
              "url": baseUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": baseUrl
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": pageData.meta_title,
                  "item": pageUrl
                }
              ]
            },
            "mainEntity": {
              "@type": "ItemList",
              "name": "Top Trending Topics",
              "description": "Current trending topics ranked by search volume",
              "numberOfItems": topTopics.length,
              "itemListElement": topTopics.map((topic, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Thing",
                  "name": topic.name.replace(/"/g, ''),
                  "description": `${topic.searchVolumeRaw.replace(/"/g, '')}`
                }
              }))
            }
          })}
        </script>

        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      <ToolSchema
        name={pageData.meta_title}
        description={pageData.meta_description}
        url={pageUrl}
        applicationCategory="AnalysisApplication"
        screenshot={pageData.cover_image ? [pageData.cover_image] : undefined}
      />

      <Header
        theme={theme}
        isAdmin={isAdmin}
        isLoggedIn={!!user}
        onLoginClick={() => {}}
        onLogout={logout}
        title="Top Best Charts"
snapshotButton={null}
      />

      <FilterMenu
        theme={theme}
        loading={loading}
        viewMode={viewMode}
        dateFilter={dateFilter}
        categoryFilter={categoryFilter}
        categories={categories}
        sourceFilter={sourceFilter}
        sources={sources.map(s => s.value)}
        maxBubbles={maxBubbles}
        searchQuery={searchQuery}
        nextBubbleIn={nextBubbleIn}
        bubbleProgress={bubbleProgress}
        bubbleLayout={bubbleLayout}
        shape={shape}
        cryptoTimeframe={cryptoTimeframe}
        showCryptoTimeframe={pageData?.source === 'coingecko_crypto'}
        showDateFilter={false}
        showCategoryFilter={false}
        onViewModeChange={setViewMode}
        onDateFilterChange={setDateFilter}
        onCategoryFilterChange={setCategoryFilter}
        onSourceFilterChange={setSourceFilter}
        onMaxBubblesChange={handleMaxBubblesChange}
        onThemeChange={handleThemeChange}
        onSearchQueryChange={setSearchQuery}
        onSearchClear={() => {
          setSearchQuery('');
          setViewMode('bubble');
        }}
        onRefresh={loadTopics}
        onBubbleLayoutChange={setBubbleLayout}
        onShapeChange={setShape}
        onCryptoTimeframeChange={setCryptoTimeframe}
        variant="homepage"
      />

      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} px-2 md:px-6 py-2 md:py-6 pb-0`}>
        <main role="main" aria-label="Trending topics visualization" style={{ minHeight: '80vh' }}>
          {loading && (
            <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</div>
          )}
          {!loading && (
            <>
              {topics.length > 0 && (
                <article className="max-w-7xl mx-auto mb-8">
                  <header>
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <h1 className={`text-xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {pageData.meta_title}
                      </h1>
                      {(viewMode === 'bubble' || viewMode === 'bar' || viewMode === 'treemap' || viewMode === 'donut') && topics.length > 0 && (
                        <div className="flex items-center gap-2">
                          {viewMode === 'bubble' && (
                            <AnimationSelector
                              theme={theme}
                              selectedAnimation={animationStyle}
                              onAnimationChange={setAnimationStyle}
                            />
                          )}
                          <ShareSnapshot
                            theme={theme}
                            canvasRef={
                              viewMode === 'bubble' ? bubbleChartRef :
                              viewMode === 'treemap' ? treemapChartRef :
                              viewMode === 'donut' ? donutChartRef :
                              barChartRef
                            }
                            variant="inline"
                          />
                        </div>
                      )}
                    </div>
                    <div className={`flex flex-wrap items-center gap-3 mb-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <time dateTime={lastUpdated.toISOString()}>
                        Last updated: {lastUpdated.toLocaleString('en-US', {
                          timeZone: 'America/New_York',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })} ET
                      </time>
                      <a
                        href="#top-trending-heading"
                        className={`px-2 py-1 rounded text-xs cursor-pointer transition-all hover:scale-105 inline-block ${theme === 'dark' ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                      >
                        View Full List of {topics.length} {sourceName}
                      </a>
                    </div>
                  </header>
                </article>
              )}
              {topics.length > 0 && viewMode === 'bubble' && (
                <div ref={bubbleChartRef} style={{ minHeight: '500px' }}>
                  <BubbleChart
                    topics={sortedTopics}
                    maxDisplay={maxBubbles}
                    theme={theme}
                    layout={bubbleLayout}
                    onBubbleTimingUpdate={handleBubbleTimingUpdate}
                    comparingTopics={comparingTopics}
                    onComparingTopicsChange={setComparingTopics}
                    useCryptoColors={pageData?.source === 'coingecko_crypto'}
                    cryptoTimeframe={cryptoTimeframe}
                    animationStyle={animationStyle}
                    shape={shape as Shape}
                  />
                </div>
              )}
              {topics.length > 0 && viewMode === 'bar' && (
                <div ref={barChartRef} className="max-w-7xl mx-auto">
                  <BarChart
                    topics={sortedTopics}
                    maxDisplay={maxBubbles}
                    theme={theme}
                    useCryptoColors={pageData?.source === 'coingecko_crypto'}
                    cryptoTimeframe={cryptoTimeframe}
                  />
                </div>
              )}
              {topics.length > 0 && viewMode === 'treemap' && (
                <div ref={treemapChartRef} className="max-w-7xl mx-auto" style={{ height: 'calc(100vh - 300px)', minHeight: '500px' }}>
                  <Treemap
                    topics={topics}
                    maxDisplay={maxBubbles}
                    theme={theme}
                    useCryptoColors={pageData?.source === 'coingecko_crypto'}
                    cryptoTimeframe={cryptoTimeframe}
                  />
                </div>
              )}
              {topics.length > 0 && viewMode === 'donut' && (
                <div ref={donutChartRef} className="max-w-7xl mx-auto" style={{ height: 'calc(100vh - 300px)', minHeight: '600px' }}>
                  <DonutChart
                    topics={sortedTopics}
                    maxDisplay={maxBubbles}
                    theme={theme}
                    useCryptoColors={pageData?.source === 'coingecko_crypto'}
                    cryptoTimeframe={cryptoTimeframe}
                  />
                </div>
              )}
              {topics.length > 0 && (viewMode === 'bubble' || viewMode === 'bar' || viewMode === 'treemap' || viewMode === 'donut') && (
                  <div className="max-w-7xl mx-auto mt-8 mb-0 md:mb-8">
                    <div className="flex flex-col lg:flex-row gap-6">
                      <section className="flex-1 lg:w-[65%]" aria-labelledby="top-trending-heading">
                        <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 shadow-md`}>
                    {!isCryptoPage && (
                      <>
                        <h2 id="top-trending-heading" className={`text-xl md:text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {topTitle}
                        </h2>
                        {(pageData.intro_text || topTopicNames) && (
                          <div
                            className={`mb-4 text-sm leading-relaxed rich-text-content ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                            dangerouslySetInnerHTML={{
                              __html: pageData.intro_text || 'Explore our interactive visualization to discover real-time search trends, analyze topic popularity, and stay updated with what\'s capturing attention right now.'
                            }}
                          />
                        )}
                        <div className="relative mb-4">
                          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="text"
                            value={topSearchQuery}
                            onChange={(e) => setTopSearchQuery(e.target.value)}
                            placeholder="Search"
                            className={`w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
                          />
                          {topSearchQuery && (
                            <button
                              onClick={() => setTopSearchQuery('')}
                              className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                              aria-label="Clear search"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                    {isCryptoPage && (
                      <>
                        <h2 id="top-trending-heading" className={`text-xl md:text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Top {topTopics.length} Gainers & Losers
                        </h2>
                        {topGainers.length > 0 && topLosers.length > 0 && (() => {
                          const timeframeLabel = cryptoTimeframe === '1h' ? '1-hour' : cryptoTimeframe === '24h' ? '24-hour' : cryptoTimeframe === '7d' ? '7-day' : cryptoTimeframe === '30d' ? '30-day' : '1-year';
                          const topGainer = topGainers[0];
                          const topLoser = topLosers[0];
                          const gainerChange = topGainer?.crypto_data?.[`change_${cryptoTimeframe}` as keyof typeof topGainer.crypto_data] as number || 0;
                          const loserChange = topLoser?.crypto_data?.[`change_${cryptoTimeframe}` as keyof typeof topLoser.crypto_data] as number || 0;
                          const gainerName = topGainer?.name.replace(/"/g, '') || '';
                          const loserName = topLoser?.name.replace(/"/g, '') || '';

                          return (
                            <p className={`mb-4 text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              Track the biggest movers in cryptocurrency markets right now. Leading the gainers is <strong className="text-green-600">{gainerName}</strong> with a {timeframeLabel} gain of <strong className="text-green-600">+{gainerChange.toFixed(2)}%</strong>, while <strong className="text-red-600">{loserName}</strong> leads the decline with <strong className="text-red-600">{loserChange.toFixed(2)}%</strong> change. Monitor real-time cryptocurrency price movements, analyze market trends, and discover top performing digital assets. Updated every 5 minutes with live data from CoinGecko, our cryptocurrency tracker helps you identify trading opportunities and stay informed about market volatility across Bitcoin, Ethereum, and thousands of altcoins.
                            </p>
                          );
                        })()}
                        <div className="relative mb-4">
                          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="text"
                            value={topSearchQuery}
                            onChange={(e) => setTopSearchQuery(e.target.value)}
                            placeholder="Search"
                            className={`w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
                          />
                          {topSearchQuery && (
                            <button
                              onClick={() => setTopSearchQuery('')}
                              className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                              aria-label="Clear search"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}

                    {!isCryptoPage && (
                      <>
                        {topTopics.length === 0 && topSearchQuery ? (
                          <div className={`rounded-lg p-8 text-center border ${theme === 'dark' ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                            <p className="text-lg">No topics found matching "{topSearchQuery}"</p>
                            <button
                              onClick={() => setTopSearchQuery('')}
                              className="mt-4 px-4 py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                            >
                              Clear Search
                            </button>
                          </div>
                        ) : (
                        <>
                        <ol className="list-none" itemScope itemType="https://schema.org/ItemList">
                          {displayTopics.map((topic, index) => {
                            return (
                          <li
                            key={index}
                            className={`px-2 py-1.5 transition-colors cursor-pointer ${index < displayTopics.length - 1 ? `border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}` : ''} ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                            itemProp="itemListElement"
                            itemScope
                            itemType="https://schema.org/ListItem"
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setListTooltipData({
                                topic,
                                x: rect.left + rect.width / 2,
                                y: rect.top,
                                rank: topic.originalRank
                              });
                            }}
                          >
                            <meta itemProp="position" content={String(topic.originalRank)} />
                            <article className="flex-1" itemProp="item" itemScope itemType="https://schema.org/Thing">
                              <div className="flex items-center gap-2 mb-0.5">
                                <div className="w-8 flex items-center justify-center flex-shrink-0" aria-label={`Rank ${topic.originalRank}`}>
                                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {topic.originalRank}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap flex-1">
                                  <h3 className={`font-bold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} itemProp="name">{topic.name.replace(/"/g, '')}</h3>
                                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} itemProp="description">
                                    {topic.searchVolumeRaw.replace(/"/g, '')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-xs pl-[40px]">
                                {topic.category && (
                                  <span className={`px-2 py-0.5 rounded text-xs ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                    {topic.category}
                                  </span>
                                )}
                                {topic.note && (
                                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {topic.note}
                                  </span>
                                )}
                              </div>
                            </article>
                          </li>
                          );
                          })}
                        </ol>
                        {totalPages > 1 && (
                          <div className="mt-4 flex items-center justify-center gap-1">
                            <button
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                              className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                              title="First page"
                            >
                              <ChevronsLeft size={16} />
                            </button>
                            <button
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                            >
                              Previous
                            </button>
                            <span className={`px-3 py-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                              className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setCurrentPage(totalPages)}
                              disabled={currentPage === totalPages}
                              className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                              title="Last page"
                            >
                              <ChevronsRight size={16} />
                            </button>
                          </div>
                        )}
                        </>
                        )}
                      </>
                    )}

                    {isCryptoPage && (
                      <>
                        {topTopics.length === 0 && topSearchQuery ? (
                          <div className="bg-gray-50 text-gray-600 rounded-lg p-8 text-center border border-gray-200">
                            <p className="text-lg">No cryptocurrencies found matching "{topSearchQuery}"</p>
                            <button
                              onClick={() => setTopSearchQuery('')}
                              className="mt-4 px-4 py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                            >
                              Clear Search
                            </button>
                          </div>
                        ) : (
                        <>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Top Gainers */}
                        <div>
                          <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-green-600">
                            <ArrowUp size={24} />
                            Gainers ({cryptoTimeframe.toUpperCase()})
                          </h3>
                          <ol className="list-none">
                            {topGainers.map((topic, index) => {
                              const change = topic.crypto_data?.[`change_${cryptoTimeframe}` as keyof typeof topic.crypto_data] || 0;
                              return (
                                <li
                                  key={index}
                                  className={`px-2 py-1.5 transition-colors cursor-pointer ${index < topGainers.length - 1 ? `border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}` : ''} ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setListTooltipData({
                                      topic,
                                      x: rect.left + rect.width / 2,
                                      y: rect.top,
                                      rank: topic.originalRank
                                    });
                                  }}
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <div className="w-6 flex items-center justify-center flex-shrink-0">
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {topic.originalRank}
                                        </div>
                                      </div>
                                      <h4 className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{topic.name.replace(/"/g, '')}</h4>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs pl-[32px]">
                                      <span className="text-green-500 font-bold">
                                        +{typeof change === 'number' ? change.toFixed(2) : change}%
                                      </span>
                                      {topic.crypto_data?.formatted?.price && (
                                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                          {topic.crypto_data.formatted.price}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        </div>

                        {/* Top Losers */}
                        <div>
                          <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-red-600">
                            <ArrowDown size={24} />
                            Losers ({cryptoTimeframe.toUpperCase()})
                          </h3>
                          <ol className="list-none">
                            {topLosers.map((topic, index) => {
                              const change = topic.crypto_data?.[`change_${cryptoTimeframe}` as keyof typeof topic.crypto_data] || 0;
                              return (
                                <li
                                  key={index}
                                  className={`px-2 py-1.5 transition-colors cursor-pointer ${index < topLosers.length - 1 ? `border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}` : ''} ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setListTooltipData({
                                      topic,
                                      x: rect.left + rect.width / 2,
                                      y: rect.top,
                                      rank: topic.originalRank
                                    });
                                  }}
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <div className="w-6 flex items-center justify-center flex-shrink-0">
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {topic.originalRank}
                                        </div>
                                      </div>
                                      <h4 className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{topic.name.replace(/"/g, '')}</h4>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs pl-[32px]">
                                      <span className="text-red-500 font-bold">
                                        {typeof change === 'number' ? change.toFixed(2) : change}%
                                      </span>
                                      {topic.crypto_data?.formatted?.price && (
                                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                          {topic.crypto_data.formatted.price}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      </div>
                      {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-center gap-1 md:gap-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className={`px-2 py-1 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition-colors ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                          >
                            Previous
                          </button>
                          <span className={`px-2 py-1 md:px-4 md:py-2 text-xs md:text-base ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-2 py-1 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-semibold transition-colors ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                          >
                            Next
                          </button>
                        </div>
                      )}
                        </>
                        )}
                      </>
                    )}
                        </div>
                      </section>

                      <aside className="lg:w-[35%]">
                        <div className={`rounded-lg p-6 sticky top-4 shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                          <div className="relative mb-6">
                            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} inline-block px-3 py-1 relative`}>
                              <span className="relative z-10">{pageData?.category?.toUpperCase() || 'FEATURED'}</span>
                              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 transform -skew-x-12"></div>
                            </h2>
                          </div>
                          {latestPages.length > 0 && (
                            <div className="space-y-6">
                              {latestPages.map((page, index) => (
                                <a
                                  key={page.id}
                                  href={page.page_url}
                                  className="block group"
                                >
                                  <div className="flex gap-3">
                                    <div className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden relative ${
                                      !page.cover_image ? 'flex items-center justify-center' : ''
                                    }`}>
                                      {page.cover_image ? (
                                        <>
                                          <img src={page.cover_image} alt={page.meta_title} className="w-full h-full object-cover" />
                                          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                            <span className="text-white font-bold text-xl">{index + 1}</span>
                                          </div>
                                        </>
                                      ) : (
                                        <div className={`w-full h-full bg-gradient-to-br ${
                                          index === 0 ? 'from-yellow-400 to-orange-500' :
                                          index === 1 ? 'from-blue-400 to-cyan-500' :
                                          index === 2 ? 'from-green-400 to-teal-500' :
                                          index === 3 ? 'from-pink-400 to-rose-500' :
                                          'from-orange-400 to-red-500'
                                        } flex items-center justify-center`}>
                                          <span className="text-white font-bold text-xl">{index + 1}</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className={`font-semibold group-hover:text-blue-600 transition-colors line-clamp-2 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                      }`}>
                                        {page.meta_title}
                                      </h3>
                                      {page.meta_description && (
                                        <p className={`text-xs mt-1 line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                          {page.meta_description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </aside>
                    </div>
                  </div>
              )}
              {topics.length > 0 && viewMode === 'list' && (
              <div className="max-w-7xl mx-auto">
                <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-md'} rounded-lg border overflow-hidden`}>
                  <div className={`hidden md:grid grid-cols-3 gap-4 px-6 py-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200'} font-semibold text-sm`}>
                    <button
                      onClick={() => handleSort('name')}
                      className={`flex items-center gap-1 hover:${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} transition-colors`}
                    >
                      Name <SortIcon field="name" />
                    </button>
                    <button
                      onClick={() => handleSort('searchVolume')}
                      className={`flex items-center justify-center gap-1 hover:${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} transition-colors`}
                    >
                      Search Volume <SortIcon field="searchVolume" />
                    </button>
                    <div className="text-center">Note</div>
                  </div>
                  <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {getSortedTopics().map((topic, index) => (
                      <div key={index} className={`${theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                        <div className="hidden md:grid grid-cols-3 gap-4 px-6 py-4">
                          <div className="font-medium">{topic.name.replace(/"/g, '')}</div>
                          <div className={`text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{topic.searchVolumeRaw.replace(/"/g, '')}</div>
                          <div className={`text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {topic.note || '-'}
                          </div>
                        </div>
                        <div className="md:hidden px-4 py-3 space-y-2">
                          <div className="font-medium text-base">{topic.name.replace(/"/g, '')}</div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Volume:</span>
                            <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                              {topic.searchVolumeRaw.replace(/"/g, '')}
                            </span>
                          </div>
                          {topic.note && (
                            <div className="flex items-start gap-2 text-xs">
                              <span className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Note:</span>
                              <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                {topic.note}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
              {topics.length === 0 && !loading && (
                <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  No trending topics found for this source.
                </div>
              )}
            </>
          )}
          {pageData.summary && (
            <section
              className="max-w-7xl mx-auto mt-8 mb-6 px-2 md:px-0"
              aria-labelledby="page-summary"
              itemScope
              itemType="https://schema.org/Article"
            >
              <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4 md:p-6`}>
                {pageData.cover_image && (
                  <div className="mb-6 rounded-xl overflow-hidden">
                    <img
                      src={pageData.cover_image}
                      alt={pageData.meta_title}
                      className="w-full h-64 md:h-96 object-cover"
                    />
                  </div>
                )}
                <div
                  itemProp="articleBody"
                  className="summary-content"
                  dangerouslySetInnerHTML={{ __html: pageData.summary }}
                />
                <meta itemProp="author" content="Top Best Charts" />
                <meta itemProp="datePublished" content={pageData.created_at} />
              </div>
            </section>
          )}
          {pageData.faq && (
            <section
              className="max-w-7xl mx-auto mt-8 mb-6 px-2 md:px-0"
              aria-labelledby="page-faq"
            >
              <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4 md:p-6`}>
                <div
                  className="summary-content"
                  dangerouslySetInnerHTML={{ __html: pageData.faq }}
                />
              </div>
            </section>
          )}
          {allLatestPages.length > 0 && (
        <section className="max-w-7xl mx-auto mt-8 mb-0 px-4 md:px-6" aria-labelledby="latest-pages-heading">
          <div className="relative mb-6">
            <h2 id="latest-pages-heading" className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} inline-block px-4 py-2 relative`}>
              <span className="relative z-10">LATEST</span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 transform -skew-x-12"></div>
            </h2>
          </div>
          <div className="space-y-4">
            {allLatestPages.map((page) => (
              <a
                key={page.id}
                href={page.page_url}
                className={`flex gap-4 p-4 rounded-lg hover:shadow-lg transition-all duration-300 ${
                  theme === 'dark' ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex-shrink-0 w-32 h-24 rounded-lg overflow-hidden">
                  {page.cover_image ? (
                    <img src={page.cover_image} alt={page.meta_title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-cyan-500"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs mb-2">
                    {page.category && (
                      <>
                        <span className={`uppercase font-semibold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                          {page.category}
                        </span>
                        <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>/</span>
                      </>
                    )}
                    <span className={`flex items-center gap-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                        <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2"/>
                      </svg>
                      {new Date(page.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <h3 className={`font-bold text-lg mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {page.meta_title}
                  </h3>
                  {page.meta_description && (
                    <p className={`text-sm line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {page.meta_description}
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

          {topics.length > 0 && faqs.length > 0 && (
            <section className="max-w-7xl mx-auto mt-12 mb-0 px-4 md:px-6" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className={`text-xl md:text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Frequently Asked Questions
              </h2>
              <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Find answers to common questions about {pageData.meta_title.toLowerCase()} and how to use our real-time trending topics platform to discover viral content, analyze search trends, and stay ahead of what's trending online.
              </p>
              <div className="space-y-6">
                {faqs.map((faq) => (
                  <details key={faq.id} className={`group ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-lg`}>
                    <summary className={`cursor-pointer px-6 py-4 font-semibold ${theme === 'dark' ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'} transition-colors list-none flex items-center justify-between`}>
                      <span>{faq.question}</span>
                      <span className="ml-2 text-2xl group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <div className={`px-6 pb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      <p>{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
      <ComparisonPanel
        topics={topics.filter(t => comparingTopics.has(t.name))}
        theme={theme}
        onClose={() => setComparingTopics(new Set())}
        onRemoveTopic={(topicName) => {
          setComparingTopics(prev => {
            const next = new Set(prev);
            next.delete(topicName);
            return next;
          });
        }}
      />

      {listTooltipData && (
        <BubbleTooltip
          topic={listTooltipData.topic}
          x={listTooltipData.x}
          y={listTooltipData.y}
          rank={listTooltipData.rank}
          theme={theme}
          isPinned={false}
          onTogglePin={() => {}}
          onCompare={() => {}}
          isComparing={false}
          onClose={() => setListTooltipData(null)}
          cryptoTimeframe={cryptoTimeframe}
        />
      )}

      <Footer key={`footer-${theme}`} theme={theme} />
    </>
  );
}

export default DynamicPage;
